import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../../helpers/ApiHelper';
import App from '../../../../model/appManagement/App';
import AppService from '../../../../services/AppService';
import LoadingSpinner from '../../../common/LoadingSpinner';

interface AppSubmissionFormProps {
    app: App;
}

const AppSubmissionForm = ({ app }: AppSubmissionFormProps) => {
    const [formUrl, setFormUrl] = useState<string | null>(null);
    const [isFormLoaded, setIsFormLoaded] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [iframeHeight, setIframeHeight] = useState(200);
    const navigate = useNavigate();

    useEffect(() => {
        api.get<any>(`Apps/${app.id}/wufoo/integrationcertification`).then((response) => {
            if (response.ok) {
                try {
                    const url = new URL(response.data.formUrl);
                    if (url.hostname.endsWith('.wufoo.com') || url.hostname === 'wufoo.com') {
                        setFormUrl(response.data.formUrl);
                    } else {
                        console.error('Invalid Wufoo form URL domain:', url.hostname);
                    }
                } catch (error) {
                    console.error('Invalid Wufoo form URL:', error);
                }
            }
        });
    }, [app.id]);

    useEffect(() => {
        // polling the server to check if the app's status has changed since we can't get any callback from the wufoo iframe form
        if (isFormLoaded) {
            let timeout = setInterval(() => {
                AppService.getApplicationById(app.id).then((response) => {
                    if (response.submissionStatus === 'Submitted') {
                        setIsFormSubmitted(true);
                        setIframeHeight(200);
                        setTimeout(() => {
                            navigate(`/Apps/${app.id}`, { state: { application: app }, replace: true });
                        }, 2000);
                    }
                });
            }, 2000);
            return () => clearTimeout(timeout);
        }
    }, [app, isFormLoaded, navigate]);

    const onFormLoad = () => {
        setIframeHeight(2000);
        setIsFormLoaded(true);
    };

    return (
        <div className="row">
            <div className="mx-auto col-md-8 m-3">
                <div className="alert alert-info mb-3" role="alert">
                    <h5 className="alert-heading">File Upload Guidelines</h5>
                    <p className="mb-2">
                        When submitting your app for approval, please ensure that any files you upload meet the
                        following requirements:
                    </p>
                    <ul className="mb-2">
                        <li>
                            <strong>Allowed file types:</strong> .pdf, .doc, .docx, .txt, .png, .jpg, .jpeg, .gif
                        </li>
                        <li>
                            <strong>Prohibited file types:</strong> Executable files (.exe, .bat, .cmd, .com, .scr,
                            .vbs, .js, .jar, .msi, .app, .deb, .rpm, .dmg, .pkg) and archive files (.zip) are not
                            allowed for security reasons
                        </li>
                        <li>All uploaded files will be scanned for malware and rejected if threats are detected</li>
                    </ul>
                    <p className="mb-0">
                        <small>
                            Files containing known malware signatures (including test files like EICAR) will be
                            automatically rejected.
                        </small>
                    </p>
                </div>
                {(!isFormLoaded || isFormSubmitted) && <LoadingSpinner />}
                {formUrl && (
                    <iframe
                        id="IntegrationCertificationForm"
                        onLoad={onFormLoad}
                        height={iframeHeight}
                        title="Embedded Wufoo Form"
                        style={{
                            width: '80%',
                            border: 'none',
                            visibility: isFormLoaded ? 'visible' : 'hidden',
                        }}
                        sandbox="allow-forms allow-scripts allow-same-origin"
                        src={formUrl}
                    >
                        <a href={formUrl}>Fill out my Wufoo form!</a>
                    </iframe>
                )}
            </div>
        </div>
    );
};

export default AppSubmissionForm;
